import { FC } from 'react';
import classnames from 'classnames';
import Button from 'shared/components/andtComponents/Button';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import Pagination from 'shared/components/andtComponents/Pagination';
import GovernanceTagsRowsDropdown from './RowsDropdown';
import styles from './TableHeader.module.scss';
import { TableHeaderProps } from '../../types';
import Tooltip from 'shared/components/andtComponents/Tooltip';

import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';

const FilterIcon = () => <GenerateIcon iconName={ICONS.barsFilter.name} />;
const FilterListIcon = () => <GenerateIcon iconName={ICONS.filterList.name} />;
const DownloadIcon = () => <GenerateIcon iconName={ICONS.download.name} />;

const GovernanceTagsTableHeader: FC<TableHeaderProps> = ({
  setters,
  flags,
  config,
  pagination,
  handleDownloadCSV,
  activeFiltersCount = 0,
}) => {
  const {
    setShowTagged,
    setShowUntagged,
    setFiltersOpen,
    setShowFilters,
    setItemsPerPage,
    setOpenRowsDropdown,
    setCurrentPage,
  } = setters;

  const { showTagged, showUntagged, filtersOpen, showFilters, openRowsDropdown, isLoadingResources } = flags;

  const { filteredDataLength, totalResourcesLength, itemsPerPage, rowsOptions, filteredData } = config;

  const { currentPage, totalPages } = pagination;

  const hasActiveFilters = activeFiltersCount > 0;
  const filterButtonText = hasActiveFilters ? `(${activeFiltersCount})` : '';

  return (
    <div className={styles.tableHeader}>
      <div className={styles.headerLeft}>
        <Button
          text={filterButtonText}
          isSecondary={!hasActiveFilters}
          automationid="open-filters"
          icon={FilterIcon}
          className={styles.headerButton}
          onClick={() => setFiltersOpen(!filtersOpen)}
        />
        <div className={styles.resourceLabel}>
          <span>
            Resources
            <span className={styles.resourceCount}>
              ({filteredDataLength}/{totalResourcesLength})
            </span>
          </span>
        </div>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id="taggedCheckbox"
            checked={showTagged}
            onChange={(e) => {
              setShowTagged(e.target.checked);
            }}
          />
          <Tooltip
            title="Resources with Virtual tags"
            placement="top"
            mode="outline"
            classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          >
            <label className={styles.checkboxLabel} htmlFor="taggedCheckbox">
              Tagged
            </label>
          </Tooltip>
        </div>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id="untaggedCheckbox"
            checked={showUntagged}
            onChange={(e) => {
              setShowUntagged(e.target.checked);
            }}
          />
          <Tooltip
            title="Resources without Virtual tags and cloud provider tags"
            placement="top"
            mode="outline"
            classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          >
            <label className={styles.checkboxLabel} htmlFor="untaggedCheckbox">
              Untagged
            </label>
          </Tooltip>
        </div>
      </div>
      <div className={styles.headerRight}>
        <Button
          text={`Filter Results`}
          isSecondary
          automationid="filter-results"
          icon={FilterListIcon}
          className={classnames(styles.headerButton, styles.transparentButton)}
          onClick={() => setShowFilters(!showFilters)}
        />
        <Button
          isSecondary
          automationid="download-csv"
          icon={DownloadIcon}
          className={classnames(styles.headerButton, styles.transparentButton)}
          onClick={handleDownloadCSV}
          disabled={!filteredData?.length}
        />
        <GovernanceTagsRowsDropdown
          itemsPerPage={itemsPerPage}
          rowsOptions={rowsOptions}
          openRowsDropdown={openRowsDropdown}
          setItemsPerPage={setItemsPerPage}
          setOpenRowsDropdown={setOpenRowsDropdown}
        />
        <Pagination
          currPageIdx={currentPage - 1}
          onPageChange={(pageIndex) => setCurrentPage(pageIndex + 1)}
          totalPages={totalPages ?? Math.ceil(filteredDataLength / itemsPerPage.value)}
          isPageLoading={isLoadingResources}
          height={24}
        />
      </div>
    </div>
  );
};

export default GovernanceTagsTableHeader;
